import React from 'react';
import {Button, Col, Modal, Row, Skeleton, Spin} from 'antd';

import CarSpecsForm from '../../../components/car/CarSpecsForm';
import CarDetailsForm from '../../../components/car/CarDetailsForm';
import CarDetailsCard from '../../../components/car/CarDetailsCard';
import CarSpecsCard from '../../../components/car/CarSpecsCard';
import ErrorBoundary from '../../../components/ErrorBoundary';
import {messageType, showMessages} from '../../../helpers/message';
import {cardsGridColumn} from '../../../assets/styles';

import {Car} from '../../../types/carTypes';
import {
    useGetCarRateClassesQuery,
    useUpdateCarMutation
} from '../../../store/api/carsApi';
import {BaseError} from '../../../types/apiTypes';
import {
    nothingToUpdate,
    changesSuccessfullySavedMessage
} from '../../../helpers/text';
import _ from 'lodash';

interface CarGeneralInfoStaffProps {
    car?: Car;
    isStaff: boolean;
}

const CarGeneralInfoStaff = ({car, isStaff = false}: CarGeneralInfoStaffProps) => {
    const [isEditingDetails, setIsEditingDetails] = React.useState(false);
    const [isEditingSpecs, setIsEditingSpecs] = React.useState(false);

    const {data: rateClasses = [], isError: isGetError, error: getError, isFetching} = useGetCarRateClassesQuery();

    const [updateCar, {
        isSuccess: isUpdateSuccess,
        isError: isUpdateError,
        error: updateError,
        reset: updateReset,
        isLoading: isUpdateLoading
    }] = useUpdateCarMutation();

    React.useEffect(() => {
        if (isGetError && getError) {
            showMessages((getError as BaseError).data?.message, messageType.error);
        }
    }, [isGetError, getError]);

    React.useEffect(() => {
        if (isUpdateSuccess) {
            showMessages(changesSuccessfullySavedMessage, messageType.success);
            setIsEditingDetails(false);
            setIsEditingSpecs(false);
            updateReset();
        } else if (isUpdateError) {
            showMessages((updateError as BaseError).data?.message, messageType.error);
            updateReset();
        }
    }, [updateError, isUpdateError, isUpdateSuccess, updateReset]);

    const updateDetails = async (
        data: Partial<Car>
    ) => {
        const payload = sanitizeDetailsPayload(data);

        if (Object.keys(payload).length === 0) {
            showMessages(nothingToUpdate, messageType.error);
            return;
        }

        if (car?.id) {
            await updateCar({carID: car.id, requestData: payload});
        }

    }

    const sanitizeDetailsPayload = (data: Partial<Car>) => {
        let payload = { ...data };

        if (car) {
            // OmitBy excludes undefined or unchanged fields and leaves only updated fields.
            payload = _.omitBy(
                data,
                (value, key) =>
                    data[key as keyof Car] === undefined ||
                    value === car[key as keyof Car]
            );
        }

        return payload;
    }

    return (
        car === undefined ?
            <Skeleton active/>
            :
            <>
                <ErrorBoundary>
                    <Row gutter={16}>
                        <Col {...cardsGridColumn}>
                            <Spin spinning={isFetching}>
                                <CarDetailsCard
                                    car={car}
                                    onEdit={setIsEditingDetails}
                                    isStaff={isStaff}
                                />
                            </Spin>
                        </Col>
                        <Col {...cardsGridColumn}>
                            <Spin spinning={isFetching}>
                                <CarSpecsCard
                                    car={car}
                                    onEdit={setIsEditingSpecs}
                                    isStaff={isStaff}
                                />
                            </Spin>
                        </Col>
                    </Row>
                    {
                        isEditingDetails &&
                        <Modal
                            title="Details"
                            open={isEditingDetails}
                            onCancel={() => {
                                setIsEditingDetails(false)
                            }}
                            footer={[
                                <Button key="cancel" onClick={() => {
                                    setIsEditingDetails(false)
                                }}>
                                    Cancel
                                </Button>,
                                <Button
                                    form="car-details"
                                    key="submit"
                                    type="primary"
                                    htmlType="submit"
                                    loading={isUpdateLoading}
                                >
                                    Save
                                </Button>,
                            ]}
                        >
                            <CarDetailsForm
                                car={car}
                                availableRateClasses={rateClasses}
                                onSubmit={updateDetails}
                            />
                        </Modal>
                    }
                    {
                        isEditingSpecs &&
                        <Modal
                            title="Specs"
                            open={isEditingSpecs}
                            onCancel={() => setIsEditingSpecs(false)}
                            footer={[
                                <Button key="cancel" onClick={() => {
                                    setIsEditingSpecs(false)
                                }}>
                                    Cancel
                                </Button>,
                                <Button
                                    form="car-specs"
                                    key="submit"
                                    type="primary"
                                    htmlType="submit"
                                    loading={isUpdateLoading}
                                >
                                    Save
                                </Button>,
                            ]}
                        >
                            <CarSpecsForm
                                car={car}
                                onSubmit={updateDetails}
                            />
                        </Modal>
                    }
                </ErrorBoundary>
            </>
    );
}

export default CarGeneralInfoStaff;
